import React, { useState } from 'react';
import { StackSimple, X } from '@phosphor-icons/react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Switch } from '../ui/switch';

// =====================================================================
// State-by-state WMS providers for Australian cadastral boundaries.
// Each provider exposes:
//   - id        unique slug
//   - state     two-letter state code (for the bbox dispatcher)
//   - imgDomain image src substring used by the CSS filter recolouring
//   - bbox      [lngMin, latMin, lngMax, latMax] rough state extent (point-in-rect
//               check is sufficient for tile dispatch; border tiles may briefly miss
//               but neighbouring states' tiles fill the gap)
//   - buildTileUrl(bboxMercator) → URL for a 256x256 PNG with transparent background
// =====================================================================

export const STATE_PROVIDERS = [
  {
    id: 'nsw', state: 'NSW', label: 'New South Wales',
    imgDomain: 'maps.six.nsw.gov.au',
    bbox: [140.95, -37.55, 153.65, -28.15],
    buildTileUrl: (bbox) =>
      'https://maps.six.nsw.gov.au/arcgis/services/public/NSW_Cadastre/MapServer/WMSServer' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=8' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
    // ArcGIS REST FeatureLayer query → GeoJSON. bbox here is [lngMin,latMin,lngMax,latMax] EPSG:4326.
    buildWfsUrl: (bbox4326) =>
      'https://maps.six.nsw.gov.au/arcgis/rest/services/public/NSW_Cadastre/MapServer/8/query' +
      `?f=geojson&geometry=${encodeURIComponent(bbox4326.join(','))}` +
      '&geometryType=esriGeometryEnvelope&spatialRel=esriSpatialRelIntersects' +
      '&inSR=4326&outSR=4326&outFields=*&returnGeometry=true&resultRecordCount=500',
  },
  {
    id: 'vic', state: 'VIC', label: 'Victoria',
    // Vicmap reference overlay — includes cadastral parcel lines plus other annotations.
    // This is a pre-rendered raster (not a cadastre-only WMS layer) so it's wider in scope
    // than NSW/QLD/etc. but it's the only free, no-auth Vicmap source available right now.
    imgDomain: 'base.maps.vic.gov.au',
    bbox: [140.96, -39.20, 149.97, -33.98],
    buildTileUrl: (bbox) =>
      'https://base.maps.vic.gov.au/wms/wms' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=CARTO_OVERLAY_WM_256' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
    // VIC OpenData GeoServer: rich V_PARCEL_MP view with Lot/Plan/Allotment/Status.
    // GeoServer WFS 2.0.0 with EPSG:4326 demands lat,lng axis order via the URN
    // CRS form. Without that suffix the server returns 0 features for any bbox.
    buildWfsUrl: (bbox4326) =>
      'https://opendata.maps.vic.gov.au/geoserver/ows' +
      '?service=WFS&version=2.0.0&request=GetFeature' +
      '&typeNames=open-data-platform:v_parcel_mp' +
      '&outputFormat=application/json&srsName=EPSG:4326' +
      `&bbox=${bbox4326[1]},${bbox4326[0]},${bbox4326[3]},${bbox4326[2]},urn:ogc:def:crs:EPSG::4326` +
      '&count=500',
  },
  {
    id: 'qld', state: 'QLD', label: 'Queensland',
    imgDomain: 'spatial-gis.information.qld.gov.au',
    bbox: [137.99, -29.18, 153.55, -9.22],
    buildTileUrl: (bbox) =>
      'https://spatial-gis.information.qld.gov.au/arcgis/services/PlanningCadastre/LandParcelPropertyFramework/MapServer/WMSServer' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=Cadastral_parcels13960' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
    // QLD ArcGIS REST FeatureLayer (Cadastral Parcels is layer 4 in the public MapServer)
    buildWfsUrl: (bbox4326) =>
      'https://spatial-gis.information.qld.gov.au/arcgis/rest/services/PlanningCadastre/LandParcelPropertyFramework/MapServer/4/query' +
      `?f=geojson&geometry=${encodeURIComponent(bbox4326.join(','))}` +
      '&geometryType=esriGeometryEnvelope&spatialRel=esriSpatialRelIntersects' +
      '&inSR=4326&outSR=4326&outFields=*&returnGeometry=true&resultRecordCount=500',
  },
  {
    id: 'wa', state: 'WA', label: 'Western Australia',
    supported: false, // SLIP Cadastre is subscription-only; no free public WMS available
    imgDomain: 'services.slip.wa.gov.au',
    bbox: [112.92, -35.20, 129.00, -13.69],
    buildTileUrl: (bbox) =>
      'https://services.slip.wa.gov.au/public/services/SLIP_Public_Services/Cadastre/MapServer/WMSServer' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=0' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
  },
  {
    id: 'sa', state: 'SA', label: 'South Australia',
    imgDomain: 'dpti.geohub.sa.gov.au',
    bbox: [129.00, -38.06, 141.00, -25.99],
    buildTileUrl: (bbox) =>
      'https://dpti.geohub.sa.gov.au/server/services/CadastreParcels/MapServer/WMSServer' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=0' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
    // SA Hosted ValCad — public no-token FeatureServer with valuation cadastre
    // polygons. Lighter attributes than NSW/QLD (no Lot/Plan), but assessment
    // number + area + state + reverse-geocoded address are enough for the panel.
    buildWfsUrl: (bbox4326) => {
      const env = encodeURIComponent(JSON.stringify({
        xmin: bbox4326[0], ymin: bbox4326[1],
        xmax: bbox4326[2], ymax: bbox4326[3],
        spatialReference: { wkid: 4326 },
      }));
      return 'https://dpti.geohub.sa.gov.au/server/rest/services/Hosted/ValCad_20230531/FeatureServer/0/query' +
        `?f=geojson&where=1%3D1&geometry=${env}` +
        '&geometryType=esriGeometryEnvelope&spatialRel=esriSpatialRelIntersects' +
        '&inSR=4326&outSR=4326&outFields=*&returnGeometry=true&resultRecordCount=500';
    },
  },
  {
    id: 'tas', state: 'TAS', label: 'Tasmania',
    imgDomain: 'services.thelist.tas.gov.au',
    bbox: [143.99, -43.65, 148.58, -39.20],
    buildTileUrl: (bbox) =>
      'https://services.thelist.tas.gov.au/arcgis/services/Public/CadastreAndAdministrative/MapServer/WMSServer' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=0' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
    // TAS LIST CadastreParcels FeatureServer. Requires the ArcGIS envelope-as-JSON
    // geometry format (the simpler comma-separated form returns 400 here). With
    // f=geojson the server happily emits GeoJSON despite being an older ArcGIS.
    buildWfsUrl: (bbox4326) => {
      const env = encodeURIComponent(JSON.stringify({
        xmin: bbox4326[0], ymin: bbox4326[1],
        xmax: bbox4326[2], ymax: bbox4326[3],
        spatialReference: { wkid: 4326 },
      }));
      return 'https://services.thelist.tas.gov.au/arcgis/rest/services/Public/CadastreParcels/MapServer/0/query' +
        `?f=geojson&where=1%3D1&geometry=${env}` +
        '&geometryType=esriGeometryEnvelope&spatialRel=esriSpatialRelIntersects' +
        '&inSR=4326&outSR=4326&outFields=*&returnGeometry=true&resultRecordCount=500';
    },
  },
  {
    id: 'nt', state: 'NT', label: 'Northern Territory',
    supported: false, // NR Maps has no public ArcGIS REST or WMS endpoint exposed
    imgDomain: 'nrmaps.nt.gov.au',
    bbox: [129.00, -26.00, 138.00, -10.97],
    buildTileUrl: (bbox) =>
      'https://nrmaps.nt.gov.au/arcgis/services/CadastralFramework/Cadastre/MapServer/WMSServer' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=0' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
  },
  {
    id: 'act', state: 'ACT', label: 'Australian Capital Territory',
    supported: false, // ACTMAPI WMS times out from external origins
    imgDomain: 'app.actmapi.act.gov.au',
    bbox: [148.70, -35.92, 149.42, -35.10],
    buildTileUrl: (bbox) =>
      'https://app.actmapi.act.gov.au/arcgis/services/Public/CadastreOrtho/MapServer/WMSServer' +
      '?SERVICE=WMS&REQUEST=GetMap&VERSION=1.3.0&LAYERS=0' +
      '&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE' +
      `&CRS=EPSG:3857&BBOX=${bbox}&WIDTH=256&HEIGHT=256`,
  },
];

// Backwards-compat default used by older callsites (delete after refactor).
export const BOUNDARY_PROVIDER = STATE_PROVIDERS[0];

// Return the first provider whose bbox contains the given lng/lat. Includes
// both fully-supported providers and `rawOnly` providers (no CORS / no edge
// processing — see the canvas tile builder for how that's handled).
export function pickProviderForLatLng(lng, lat) {
  for (const p of STATE_PROVIDERS) {
    if (p.supported === false) continue;
    const [x0, y0, x1, y1] = p.bbox;
    if (lng >= x0 && lng <= x1 && lat >= y0 && lat <= y1) return p;
  }
  return null;
}

// Returns the unsupported provider whose bbox covers the point, or null.
// Used by the BoundariesControl popover to surface a "coming soon" hint when
// the user is viewing a region we can't serve yet.
export function pickUnsupportedRegion(lng, lat) {
  for (const p of STATE_PROVIDERS) {
    if (p.supported !== false) continue;
    const [x0, y0, x1, y1] = p.bbox;
    if (lng >= x0 && lng <= x1 && lat >= y0 && lat <= y1) return p;
  }
  return null;
}

// 9 colour swatches with a CSS filter chain that recolours the WMS tile (which
// returns grayscale lines on a transparent background) to the target colour.
// Filter chains were computed via https://codepen.io/sosuke/pen/Pjoqqp and
// prefixed with `brightness(0) saturate(100%)` to first flatten the source to
// pure black so the recipe lands on an exact starting point regardless of the
// upstream line shade.
const PREFIX = 'brightness(0) saturate(100%) ';
export const BOUNDARY_COLOURS = [
  { name: 'Red',    css: '#EA5455', filter: PREFIX + 'invert(53%) sepia(79%) saturate(3263%) hue-rotate(330deg) brightness(94%) contrast(96%)' },
  { name: 'Orange', css: '#FF9F43', filter: PREFIX + 'invert(75%) sepia(58%) saturate(1180%) hue-rotate(338deg) brightness(101%) contrast(101%)' },
  { name: 'Yellow', css: '#FFE66D', filter: PREFIX + 'invert(89%) sepia(85%) saturate(351%) hue-rotate(346deg) brightness(106%) contrast(102%)' },
  { name: 'Green',  css: '#28C76F', filter: PREFIX + 'invert(56%) sepia(85%) saturate(434%) hue-rotate(108deg) brightness(94%) contrast(85%)' },
  { name: 'Cyan',   css: '#00CFE8', filter: PREFIX + 'invert(63%) sepia(99%) saturate(523%) hue-rotate(154deg) brightness(94%) contrast(101%)' },
  { name: 'Blue',   css: '#3B82F6', filter: PREFIX + 'invert(40%) sepia(63%) saturate(1789%) hue-rotate(204deg) brightness(101%) contrast(94%)' },
  { name: 'Purple', css: '#7367F0', filter: PREFIX + 'invert(43%) sepia(73%) saturate(1956%) hue-rotate(229deg) brightness(98%) contrast(94%)' },
  { name: 'White',  css: '#FFFFFF', filter: 'brightness(0) saturate(100%) invert(100%)' },
  { name: 'Black',  css: '#000000', filter: 'brightness(0)' },
];

export default function BoundariesControl({
  enabled, opacity, colourIndex, panelCls,
  onToggle, onOpacityChange, onColourChange,
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          title="Property Boundaries"
          className={`w-8 h-8 flex items-center justify-center rounded-md transition-all relative ${
            enabled ? 'bg-primary text-white shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300'
          }`}
          data-testid="tool-boundaries"
        >
          <StackSimple size={18} weight={enabled ? 'fill' : 'regular'} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={`w-72 p-4 rounded-lg ${panelCls?.includes('bg-[#2F3349]') ? 'bg-[#2F3349] border-[#434968] text-[#CFD3EC]' : ''}`}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-sm font-bold">Boundaries</div>
            <p className="text-[11px] opacity-60 mt-0.5 leading-relaxed">
              Display Australian property boundary lines (cadastre) over the map.
            </p>
          </div>
          <button onClick={() => setOpen(false)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-white/10 opacity-50 hover:opacity-100">
            <X size={12} weight="bold" />
          </button>
        </div>

        <div className="flex items-center justify-between py-2.5 mt-2 border-t border-gray-200 dark:border-gray-600">
          <span className="text-xs font-semibold">Show boundaries</span>
          <Switch checked={enabled} onCheckedChange={onToggle} data-testid="boundaries-toggle" />
        </div>

        <div className={`pt-3 border-t border-gray-200 dark:border-gray-600 ${enabled ? '' : 'opacity-40 pointer-events-none'}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Colour</div>
          <div className="grid grid-cols-9 gap-1.5">
            {BOUNDARY_COLOURS.map((c, i) => {
              const active = colourIndex === i;
              const isLight = c.css.toLowerCase() === '#ffffff';
              return (
                <button
                  key={c.name}
                  onClick={() => onColourChange(i)}
                  title={c.name}
                  className={`w-6 h-6 rounded-full transition-all ${active ? 'ring-2 ring-offset-2 ring-primary' : 'hover:scale-110'} ${isLight ? 'border border-gray-300' : ''}`}
                  style={{ background: c.css }}
                  data-testid={`boundaries-colour-${c.name.toLowerCase()}`}
                />
              );
            })}
          </div>
        </div>

        <div className={`pt-3 mt-3 border-t border-gray-200 dark:border-gray-600 ${enabled ? '' : 'opacity-40 pointer-events-none'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Background opacity</span>
            <span className="text-[10px] opacity-60 tabular-nums">{Math.round(opacity * 100)}%</span>
          </div>
          <input
            type="range"
            min="0" max="100" step="5"
            value={Math.round(opacity * 100)}
            onChange={(e) => onOpacityChange(parseInt(e.target.value, 10) / 100)}
            className="w-full accent-primary"
            data-testid="boundaries-opacity-slider"
          />
          <p className="mt-1.5 text-[10px] opacity-50 leading-relaxed">
            Adds a coloured wash beneath the lines. Keep at 0% for satellite-only context.
          </p>
        </div>

        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Coverage</div>
          <div className="grid grid-cols-4 gap-1.5" data-testid="boundaries-coverage">
            {STATE_PROVIDERS.map((p) => {
              const isSoon = p.supported === false;
              return (
                <span
                  key={p.id}
                  title={isSoon ? `${p.label} — coming soon` : p.label}
                  className={`relative flex items-center justify-center px-1.5 py-1 rounded text-[10px] font-semibold ${
                    isSoon
                      ? 'bg-gray-100 dark:bg-white/5 text-gray-400 dark:text-gray-500'
                      : 'bg-success/10 text-success'
                  }`}
                  data-testid={`boundaries-state-${p.id}${isSoon ? '-soon' : ''}`}
                >
                  {p.state}
                  {isSoon && <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden="true" />}
                </span>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] opacity-50 leading-relaxed">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle mr-1" /> dot = pending upstream public WMS access. Parcel-click info: NSW, VIC, QLD, SA, TAS.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
